import { InsightsOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { periodForYear, ResolvedPeriod, yearOptions } from '../../../library/dashboardPeriod';
import {
    formatMoney,
    INCOME_MODE_HELP,
    INCOME_MODE_LABELS,
    therapistLabel,
    therapistShare,
} from '../../../library/dashboardDisplay';
import { formatRecordDate } from '../../../library/dateTimeHelper';
import { IClinicSummary, IncomeMode, incomeOf } from '../../../library/models/clinicDashboard';
import { useIsCompact } from '../../../library/useIsCompact';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { RecordCard } from '../../recordCard/RecordCard';
import { cardListStyles } from '../../recordCard/recordCard.styles';
import { KpiRow } from './KpiRow';
import { MonthlyIncomeChart } from './MonthlyIncomeChart';
import { MonthlyPatientsChart } from './MonthlyPatientsChart';
import {
    alertStyles,
    controlGroupStyles,
    controlLabelStyles,
    controlsBoxStyles,
    emptyStateBoxStyles,
    emptyStateIconStyles,
    headerBoxStyles,
    headerCellStyles,
    loadingBoxStyles,
    numberCellStyles,
    numberHeaderCellStyles,
    rangeCaptionStyles,
    subtitleStyles,
    tableContainerStyles,
    titleStyles,
    toggleGroupStyles,
    unassignedCellStyles,
} from './clinicDashboard.styles';

const INCOME_MODES: IncomeMode[] = ['BILLED', 'EARNED'];

const YEARS: number[] = yearOptions();

/**
 * A clinic administrator's view of how the practice is doing: what it took, what it
 * delivered, and who it saw.
 *
 * The page has two controls and they work differently on purpose.
 *
 * The **year** is part of the request. Changing it changes the query key, so react-query
 * fetches it and caches it per year, and going back to one already seen is instant.
 *
 * The **income mode** is not. Both readings of income are in every response, so switching
 * between them is local state and provably not a round trip -- which is the reason the API
 * answers with both rather than taking a parameter. It drives three things: the income
 * tile, the bars on the income chart, and the Income column in the table. Everything else
 * on the page means the same thing either way and must not move when it changes.
 */
export const ClinicDashboard = (): JSX.Element => {
    const theme = useTheme();
    const { t: translate, i18n } = useTranslation();
    const isCompact = useIsCompact();

    const [year, setYear] = useState<number>(YEARS[0]);
    const [mode, setMode] = useState<IncomeMode>('BILLED');

    const period: ResolvedPeriod = useMemo(() => periodForYear(year), [year]);

    const { data, isLoading } = useReactQuery<IClinicSummary>(
        services.dashboard.getClinicSummary(period.from, period.to)
    );

    /**
     * Whether the numbers were actually fetched, which is not the same as "isError is
     * false". React Query settles a query it believes cannot reach the network with
     * neither data nor error, and reading that as an empty result would tell an
     * administrator their clinic earned nothing.
     */
    const hasLoaded: boolean = data !== undefined;
    const therapists = useMemo(() => data?.therapists ?? [], [data]);

    const hasActivity: boolean =
        hasLoaded && (therapists.length > 0 || (data?.totals.activePatients ?? 0) > 0);

    return (
        <Box>
            <Box sx={headerBoxStyles}>
                <Box>
                    <Typography variant="h4" component="h1" sx={titleStyles}>
                        {translate(ResourcesIds.CLINIC_DASHBOARD_TITLE)}
                    </Typography>
                    <Typography variant="body1" sx={subtitleStyles}>
                        {translate(ResourcesIds.CLINIC_DASHBOARD_SUBTITLE)}
                    </Typography>
                </Box>
            </Box>

            <Box sx={controlsBoxStyles}>
                <Box sx={controlGroupStyles}>
                    <Typography variant="body2" sx={controlLabelStyles}>
                        {translate(ResourcesIds.DASHBOARD_YEAR_LABEL)}
                    </Typography>

                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        color="primary"
                        value={year}
                        sx={toggleGroupStyles}
                        aria-label={translate(ResourcesIds.DASHBOARD_YEAR_LABEL)}
                        onChange={(_event, next: number | null) => next && setYear(next)}
                    >
                        {YEARS.map(option => (
                            <ToggleButton key={option} value={option}>
                                {option}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                <Box sx={controlGroupStyles}>
                    <Typography variant="body2" sx={controlLabelStyles}>
                        {translate(ResourcesIds.DASHBOARD_INCOME_MODE_LABEL)}
                    </Typography>

                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        color="primary"
                        value={mode}
                        sx={toggleGroupStyles}
                        aria-label={translate(ResourcesIds.DASHBOARD_INCOME_MODE_LABEL)}
                        onChange={(_event, next: IncomeMode | null) => next && setMode(next)}
                    >
                        {INCOME_MODES.map(option => (
                            // describeChild, so the explanation is announced *in addition*
                            // to the button's own label. Without it MUI puts the title in
                            // aria-label, and a screen reader hears the whole paragraph
                            // where the button should have said "Earned".
                            <Tooltip
                                key={option}
                                describeChild
                                title={translate(INCOME_MODE_HELP[option])}
                                enterTouchDelay={0}
                            >
                                <ToggleButton value={option}>
                                    {translate(INCOME_MODE_LABELS[option])}
                                </ToggleButton>
                            </Tooltip>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                {/* The resolved dates, so the year is never the only statement of what is
                    on screen -- and so it is visible that a year in progress still runs to
                    December, which is what the deferred figure is measured at. */}
                <Typography variant="caption" sx={rangeCaptionStyles}>
                    {translate(ResourcesIds.DASHBOARD_PERIOD_RANGE, {
                        from: formatRecordDate(period.from, i18n),
                        to: formatRecordDate(period.to, i18n),
                    })}
                </Typography>
            </Box>

            {!isLoading && !hasLoaded && (
                <Alert severity="error" sx={alertStyles}>
                    {translate(ResourcesIds.CLINIC_DASHBOARD_LOAD_ERROR)}
                </Alert>
            )}

            {isLoading ? (
                <Box sx={loadingBoxStyles}>
                    <CircularProgress size={60} />
                </Box>
            ) : !hasLoaded || !data ? null : !hasActivity ? (
                <Box sx={emptyStateBoxStyles}>
                    <InsightsOutlined sx={emptyStateIconStyles(theme)} />
                    <Typography variant="h6" gutterBottom>
                        {translate(ResourcesIds.CLINIC_DASHBOARD_EMPTY_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={subtitleStyles}>
                        {translate(ResourcesIds.CLINIC_DASHBOARD_EMPTY_SUBTITLE)}
                    </Typography>
                </Box>
            ) : (
                <>
                    <KpiRow totals={data.totals} mode={mode} />

                    <MonthlyIncomeChart
                        trend={data.trend}
                        therapistTrend={data.therapistTrend}
                        mode={mode}
                    />

                    <MonthlyPatientsChart trend={data.trend} />

                    {/* Every number both charts encode, as text. This is the mobile
                        fallback and the accessibility backstop, and it is also where
                        active patients lives -- new plus existing do not add up to it,
                        because a patient seen by two therapists is active for both.

                        It could not be much of a mobile fallback while it was six columns
                        wide inside a phone: at 375px the table wanted 593px, so the last
                        three figures were off the side of the thing that existed to be
                        the readable version. Below `md` it is a card per therapist. */}
                    {isCompact ? (
                        <Box sx={cardListStyles}>
                            {therapists.map(row => (
                                <RecordCard
                                    key={row.therapistId ?? 'unassigned'}
                                    title={therapistLabel(row, translate)}
                                    fields={[
                                        {
                                            label: translate(INCOME_MODE_LABELS[mode]),
                                            value: formatMoney(incomeOf(row, mode), translate),
                                        },
                                        {
                                            label: translate(ResourcesIds.DASHBOARD_TABLE_THERAPIST_SHARE),
                                            value: therapistShare(row, mode, translate),
                                        },
                                        {
                                            label: translate(ResourcesIds.DASHBOARD_KPI_SESSIONS_HELD),
                                            value: row.sessionsHeld,
                                        },
                                        {
                                            label: translate(ResourcesIds.DASHBOARD_TABLE_NEW),
                                            value: row.newPatients,
                                        },
                                        {
                                            label: translate(ResourcesIds.DASHBOARD_TABLE_RETURNING),
                                            value: row.existingPatients,
                                        },
                                        {
                                            label: translate(ResourcesIds.DASHBOARD_KPI_ACTIVE_PATIENTS),
                                            value: row.activePatients,
                                        },
                                    ]}
                                />
                            ))}
                        </Box>
                    ) : (
                    <TableContainer component={Paper} sx={tableContainerStyles}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headerCellStyles}>
                                        {translate(ResourcesIds.DASHBOARD_TABLE_THERAPIST)}
                                    </TableCell>
                                    <TableCell sx={numberHeaderCellStyles}>
                                        {translate(INCOME_MODE_LABELS[mode])}
                                    </TableCell>
                                    <TableCell sx={numberHeaderCellStyles}>
                                        {translate(ResourcesIds.DASHBOARD_TABLE_THERAPIST_SHARE)}
                                    </TableCell>
                                    <TableCell sx={numberHeaderCellStyles}>
                                        {translate(ResourcesIds.DASHBOARD_KPI_SESSIONS_HELD)}
                                    </TableCell>
                                    <TableCell sx={numberHeaderCellStyles}>
                                        {translate(ResourcesIds.DASHBOARD_TABLE_NEW)}
                                    </TableCell>
                                    <TableCell sx={numberHeaderCellStyles}>
                                        {translate(ResourcesIds.DASHBOARD_TABLE_RETURNING)}
                                    </TableCell>
                                    <TableCell sx={numberHeaderCellStyles}>
                                        {translate(ResourcesIds.DASHBOARD_KPI_ACTIVE_PATIENTS)}
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {therapists.map(row => (
                                    <TableRow key={row.therapistId ?? 'unassigned'}>
                                        <TableCell
                                            sx={row.therapistId === null ? unassignedCellStyles : undefined}
                                        >
                                            {therapistLabel(row, translate)}
                                        </TableCell>
                                        <TableCell sx={numberCellStyles}>
                                            {formatMoney(incomeOf(row, mode), translate)}
                                        </TableCell>
                                        <TableCell sx={numberCellStyles}>
                                            {therapistShare(row, mode, translate)}
                                        </TableCell>
                                        <TableCell sx={numberCellStyles}>{row.sessionsHeld}</TableCell>
                                        <TableCell sx={numberCellStyles}>{row.newPatients}</TableCell>
                                        <TableCell sx={numberCellStyles}>{row.existingPatients}</TableCell>
                                        <TableCell sx={numberCellStyles}>{row.activePatients}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    )}
                </>
            )}
        </Box>
    );
};
